import { getPipelineTab } from './UnderwriterDashboard';
import { productLabel, getForeclosureName } from '../applications/loanFieldConstants';
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Alert, AlertDescription } from '../ui/alert';
import { Input } from '../ui/input';
import { 
  Users,
  FileText,
  Building2,
  Clock,
  CheckCircle,
  XCircle,
  UserCheck,
  UserX,
  Eye,
  AlertCircle,
  Plus,
  Edit2,
  Trash2,
  Home,
  BarChart3,
  MessageSquare,
  Mail,
  RefreshCw,
  DollarSign,
  Search,
  X,
  LogIn
} from 'lucide-react';
import ApplicationReview from '../applications/ApplicationReview';
import EditApplicationModal from '../applications/EditApplicationModal';
import BrokerToolkit from '../toolkit/BrokerToolkit';
import MessagingPanel from '../messaging/MessagingPanel';
import EditUserModal from '../admin/EditUserModal';
import CreateUserModal from '../admin/CreateUserModal';
import BrokerReportPanel from '../admin/BrokerReportPanel';
import SubBrokerManager from '../admin/SubBrokerManager';
import DashboardAIAssistant from '../dashboard/DashboardAIAssistant';
import TaskManager from '../tasks/TaskManager';
import AdminCommissions from '../commissions/AdminCommissions';
import RecycleBin from '../admin/RecycleBin';
import UserDiagnosticTool from '../admin/UserDiagnosticTool';
import axios from 'axios';

import { StatusBadge } from '../shared/StatusBadge';

const AdminDashboard = () => {
  const { user, getDashboardStats, getUserApplications, ws, impersonateUser } = useAuth();
  const [stats, setStats] = useState({});
  const [pendingUsers, setPendingUsers] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [pipelineTab, setPipelineTab] = useState('active');
  const [editingApplication, setEditingApplication] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogsLoading, setEmailLogsLoading] = useState(false);
  const [selectedMessageAppId, setSelectedMessageAppId] = useState(null);
  const [pendingTaskCount, setPendingTaskCount] = useState(0);
  // Users tab filters
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');

  // Fetch pending task count on mount
  useEffect(() => {
    if (!user) return;
    const fetchTaskCount = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/tasks`);
        const pending = (res.data.tasks || []).filter(t => t.status === 'pending').length;
        setPendingTaskCount(pending);
      } catch (err) {
        console.error('Error fetching task count:', err);
      }
    };
    fetchTaskCount();
  }, [user]);
  const [previousTab, setPreviousTab] = useState(null);
  const [previousContext, setPreviousContext] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [managingSubBrokers, setManagingSubBrokers] = useState(null);
  const [allBrokers, setAllBrokers] = useState([]);
  const [brokerLookup, setBrokerLookup] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchUnreadCounts();
    const interval = setInterval(fetchUnreadCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  // WebSocket: refresh unread counts on new message, refresh data on status change
  useEffect(() => {
    if (!ws) return;
    const removeListener = ws.addListener((data) => {
      if (data.type === 'new_message') {
        fetchUnreadCounts();
      }
      if (data.type === 'status_change') {
        fetchData();
      }
    });
    return removeListener;
  }, [ws]);

  const fetchUnreadCounts = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/messages/conversations`);
      const counts = {};
      (response.data.conversations || []).forEach(c => {
        if (c.unread_count > 0) counts[c.application_id] = c.unread_count;
      });
      setUnreadCounts(counts);
    } catch (error) { /* silent */ }
  };

  const fetchData = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const [statsData, pendingData, activeData, appsData, brokersRes] = await Promise.all([
        getDashboardStats(),
        fetchPendingUsers(),
        fetchActiveUsers(),
        getUserApplications(),
        axios.get(`${BACKEND_URL}/api/brokers`).catch(() => ({ data: [] }))
      ]);
      setStats(statsData);
      setPendingUsers(pendingData);
      setActiveUsers(activeData);
      // Build broker name lookup
      const lookup = {};
      (brokersRes.data || []).forEach(b => { lookup[b.id] = b.name; });
      setBrokerLookup(lookup);
      setAllBrokers(brokersRes.data || []);
      setApplications(appsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailLogs = async () => {
    setEmailLogsLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const resp = await axios.get(`${BACKEND_URL}/api/email/logs`);
      setEmailLogs(resp.data.logs || []);
    } catch (error) {
      console.error('Error fetching email logs:', error);
    } finally {
      setEmailLogsLoading(false);
    }
  };


  const fetchPendingUsers = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/users/pending`);
      return response.data.users || [];
    } catch (error) {
      console.error('Error fetching pending users:', error);
      return [];
    }
  };

  const fetchActiveUsers = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/users/active`);
      return response.data.users || [];
    } catch (error) {
      console.error('Error fetching active users:', error);
      return [];
    }
  };

  const handleUserApproval = async (userId, status) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${BACKEND_URL}/api/users/approve`, {
        user_id: userId,
        status: status
      });
      
      // Refresh pending users and stats
      const updatedPending = await fetchPendingUsers();
      setPendingUsers(updatedPending);
      
      // Refresh stats to update pending count
      const updatedStats = await getDashboardStats();
      setStats(updatedStats);
      
      alert(`✅ User ${status} successfully!`);
    } catch (error) {
      console.error('Error updating user status:', error);
      alert('❌ Error updating user status. Please try again.');
    }
  };

  const handleApplicationStatusUpdate = (appId, newStatus) => {
    setApplications(prev => prev.map(app => 
      app.id === appId ? { ...app, status: newStatus } : app
    ));
    setSelectedApplication(null);
    // Refresh stats after status update
    fetchData();
  };

  const handleDeleteApplication = async (appId) => {
    if (!window.confirm('Are you sure you want to delete this application? This action cannot be undone.')) {
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('auth_token');
      
      await fetch(`${backendUrl}/api/applications/${appId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      // Remove the application from the list
      setApplications(prev => prev.filter(app => app.id !== appId));
      
      // Refresh stats
      fetchData();
      
      alert('✅ Application deleted successfully');
    } catch (error) {
      console.error('Error deleting application:', error);
      alert('❌ Error deleting application. Please try again.');
    }
  };

  const getBusinessName = (app) => {
    return app.term_loan_data?.legal_corporate_name || 
           app.term_loan_data?.business_name ||
           app.term_loan_10yr_data?.legal_corporate_name ||
           app.term_loan_10yr_data?.business_name ||
           app.term_loan_3_5yr_data?.legal_corporate_name ||
           app.term_loan_3_5yr_data?.business_name ||
           app.growth_term_loan_data?.business_name ||
           app.sba_loan_data?.business_legal_name || 
           app.sba_loan_data?.business_name ||
           app.line_of_credit_data?.business_name || 
           app.equipment_financing_data?.business_name ||
           app.merchant_processing_data?.company_name ||
           app.merchant_processing_data?.business_legal_name ||
           app.merchant_processing_data?.business_name ||
           app.jumbo_term_loan_data?.business_legal_name ||
           app.jumbo_term_loan_data?.business_dba_name ||
           app.real_estate_financing_data?.legal_name_of_business ||
           app.debt_consulting_data?.legal_business_name ||
           app.debt_consulting_data?.company_legal_name ||
           app.debt_consulting_data?.dba ||
           app.debt_consulting_data?.company_dba ||
           app.family_office_data?.legal_business_name ||
           app.family_office_data?.dba ||
           getForeclosureName(app) ||
           (app.transportation_factoring_data?.first_name && app.transportation_factoring_data?.last_name
             ? `${app.transportation_factoring_data.first_name} ${app.transportation_factoring_data.last_name}` : null) ||
           app.transportation_factoring_data?.first_name ||
           app.business_name ||
           app.applicant_name ||
           'Business Application';
  };

  const getLoanAmount = (app) => {
    return app.term_loan_data?.funding_amount_requested || 
           app.term_loan_10yr_data?.funding_amount_requested ||
           app.term_loc_hybrid_data?.funding_amount_requested ||
           app.term_loc_hybrid_data?.total_loan_amount ||
           app.term_loan_3_5yr_data?.total_loan_amount ||
           app.sba_loan_data?.loan_requested_amount || 
           app.line_of_credit_data?.requested_line_of_credit_amount || 
           app.equipment_financing_data?.amount_requested ||
           app.merchant_processing_data?.amount_requested ||
           app.jumbo_term_loan_data?.amount_requested ||
           app.real_estate_financing_data?.financing_amount_requested ||
           app.debt_consulting_data?.requested_funding_amount ||
           app.debt_consulting_data?.amount_of_debt_to_refinance ||
           app.family_office_data?.requested_funding_amount ||
           app.foreclosure_bailout_data?.loan_amount || 0;
  };

  const openMessagesForApp = (appId) => {
    const ctx = { tab: activeTab };
    if (selectedApplication) { ctx.reviewApp = selectedApplication; setSelectedApplication(null); }
    if (editingApplication) { ctx.editApp = editingApplication; setEditingApplication(null); }
    setPreviousTab(activeTab);
    setPreviousContext(ctx);
    setSelectedMessageAppId(appId);
    setActiveTab('messages');
  };

  const handleBackFromMessages = () => {
    if (previousContext) {
      setActiveTab(previousContext.tab || 'overview');
      if (previousContext.reviewApp) setSelectedApplication(previousContext.reviewApp);
      if (previousContext.editApp) setEditingApplication(previousContext.editApp);
    } else {
      setActiveTab(previousTab || 'overview');
    }
    setPreviousTab(null);
    setPreviousContext(null);
    setSelectedMessageAppId(null);
  };

  const handleAIAction = useCallback((action) => {
    if (!action) return;
    const { type, value } = action;
    switch (type) {
      case 'navigate_tab':
        setActiveTab(value === 'dashboard' ? 'overview' : value);
        break;
      case 'open_review': {
        const app = applications.find(a => a.id === value);
        if (app) setSelectedApplication(app);
        break;
      }
      case 'open_edit': {
        const app = applications.find(a => a.id === value);
        if (app) setEditingApplication(app);
        break;
      }
      case 'filter_status':
        setActiveTab('applications');
        break;
      case 'search':
        setActiveTab('applications');
        break;
      case 'refresh_data':
        fetchData();
        break;
      default:
        break;
    }
  }, [applications]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-slate-800">Admin Dashboard</h2>
        <p className="text-slate-600">System overview and management</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total Applications</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-800">{stats.total_applications || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Active Brokers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.total_brokers || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Active Underwriters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{stats.total_underwriters || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Pending Approvals</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{pendingUsers.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={(val) => { setActiveTab(val); if (val !== 'messages') { setSelectedMessageAppId(null); setPreviousTab(null); setPreviousContext(null); } }}>
        <TabsList className="grid w-full grid-cols-9">
          <TabsTrigger value="overview" data-testid="admin-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="applications" data-testid="admin-tab-applications">Applications</TabsTrigger>
          <TabsTrigger value="commissions" data-testid="admin-tab-commissions">Commissions</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="admin-tab-tasks" className="relative">
            Tasks
            {pendingTaskCount > 0 && (
              <span className="ml-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-bold px-1">
                {pendingTaskCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="emails" data-testid="admin-tab-emails" onClick={() => { if (emailLogs.length === 0) fetchEmailLogs(); }}>Email Log</TabsTrigger>
          <TabsTrigger value="messages" data-testid="admin-tab-messages">Messages</TabsTrigger>
          <TabsTrigger value="toolkit" data-testid="admin-tab-toolkit">Tools</TabsTrigger>
          <TabsTrigger value="recycle" data-testid="admin-tab-recycle">Recycle Bin</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Application Status Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(stats.by_status || {}).map(([status, count]) => (
                    <div key={status} className="flex justify-between items-center">
                      <span className="text-sm text-slate-600 capitalize">{status.replace('_', ' ')}</span>
                      <Badge variant="outline">{count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Product Type Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(stats.by_product || {}).map(([product, count]) => (
                    <div key={product} className="flex justify-between items-center">
                      <span className="text-sm text-slate-600 capitalize">{product.replace('_', ' ')}</span>
                      <Badge variant="outline">{count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <div className="space-y-6">
            <BrokerReportPanel sharedSearch={userSearch} onSearchChange={setUserSearch} />
            {/* Filter bar */}
            {(() => {
              const matchesFilter = (u) => {
                const q = userSearch.trim().toLowerCase();
                if (q) {
                  const hay = `${u.name || ''} ${u.email || ''} ${u.company_name || ''}`.toLowerCase();
                  if (!hay.includes(q)) return false;
                }
                if (userRoleFilter !== 'all') {
                  if (!(u.roles || []).includes(userRoleFilter)) return false;
                }
                return true;
              };
              const filteredPending = pendingUsers.filter(matchesFilter);
              const filteredActive = activeUsers.filter(matchesFilter);
              const totalShown = filteredPending.length + filteredActive.length;
              const totalAll = pendingUsers.length + activeUsers.length;
              return (
                <>
                  <Card data-testid="users-filter-bar">
                    <CardContent className="pt-5 pb-5">
                      <div className="flex flex-col md:flex-row md:items-center gap-3">
                        <div className="relative flex-1">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                          <Input
                            type="text"
                            placeholder="Search by name, email, or company..."
                            value={userSearch}
                            onChange={(e) => setUserSearch(e.target.value)}
                            className="pl-9 pr-9"
                            data-testid="users-search-input"
                          />
                          {userSearch && (
                            <button
                              type="button"
                              onClick={() => setUserSearch('')}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 rounded"
                              title="Clear search"
                              data-testid="users-search-clear"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {[
                            { value: 'all', label: 'All' },
                            { value: 'admin', label: 'Admin' },
                            { value: 'broker', label: 'Broker' },
                            { value: 'underwriter', label: 'Underwriter' },
                            { value: 'jr_underwriter', label: 'Jr UW' },
                            { value: 'borrower', label: 'Borrower' },
                          ].map(opt => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setUserRoleFilter(opt.value)}
                              className={`text-xs font-medium px-3 py-1.5 rounded-md border transition-colors ${
                                userRoleFilter === opt.value
                                  ? 'bg-[#0E333F] border-[#0E333F] text-white'
                                  : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
                              }`}
                              data-testid={`users-filter-${opt.value}`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      {(userSearch || userRoleFilter !== 'all') && (
                        <p className="text-xs text-slate-500 mt-3" data-testid="users-filter-summary">
                          Showing {totalShown} of {totalAll} users
                          {userSearch && <> matching "<span className="font-medium text-slate-700">{userSearch}</span>"</>}
                          {userRoleFilter !== 'all' && <> with role <span className="font-medium text-slate-700">{userRoleFilter}</span></>}.
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  {/* User Account Diagnostic + Recovery — for stuck accounts */}
                  <UserDiagnosticTool onPurged={() => { fetchPendingUsers(); fetchActiveUsers(); }} />

                  {/* Pending Users */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Clock className="h-5 w-5 text-orange-500" />
                        Pending User Approvals ({filteredPending.length}{filteredPending.length !== pendingUsers.length && <span className="text-slate-400 font-normal"> of {pendingUsers.length}</span>})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {filteredPending.length === 0 ? (
                        <div className="text-center py-6 text-slate-500" data-testid="pending-users-empty">
                          <CheckCircle className="h-12 w-12 mx-auto text-slate-300 mb-2" />
                          <p className="text-slate-600">{pendingUsers.length === 0 ? 'No pending user registrations to review.' : 'No pending users match the current filter.'}</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {filteredPending.map((userToApprove) => (
                            <div key={userToApprove.id} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg" data-testid={`pending-user-${userToApprove.id}`}>
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-1">
                                  <h3 className="font-medium text-slate-800">{userToApprove.name}</h3>
                                  <div className="flex gap-1">
                                    {userToApprove.roles.map((role) => (
                                      <Badge key={role} variant="outline" className="text-xs">
                                        {role}
                                      </Badge>
                                    ))}
                                  </div>
                                  {userToApprove._unparsed && (
                                    <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-200" data-testid={`unparsed-flag-${userToApprove.id}`} title={userToApprove._parse_error || 'Validation issue — review carefully before approving'}>
                                      ⚠ Data Issue
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-slate-600">
                                  {userToApprove.email} • Registered {new Date(userToApprove.created_at).toLocaleDateString()}
                                </p>
                                {userToApprove.company_name && (
                                  <p className="text-sm text-slate-500">Company: {userToApprove.company_name}</p>
                                )}
                                {userToApprove._unparsed && userToApprove._parse_error && (
                                  <p className="text-[11px] text-amber-700 mt-1 font-mono" data-testid={`parse-error-${userToApprove.id}`}>
                                    {userToApprove._parse_error}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <Button 
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => handleUserApproval(userToApprove.id, 'approved')}
                                >
                                  <UserCheck className="h-4 w-4 mr-1" />
                                  Approve
                                </Button>
                                <Button 
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => handleUserApproval(userToApprove.id, 'rejected')}
                                >
                                  <UserX className="h-4 w-4 mr-1" />
                                  Reject
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Active Users */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Users className="h-5 w-5 text-green-500" />
                          Active Users ({filteredActive.length}{filteredActive.length !== activeUsers.length && <span className="text-slate-400 font-normal"> of {activeUsers.length}</span>})
                        </div>
                        <Button
                          onClick={() => setShowCreateUser(true)}
                          className="bg-teal-600 hover:bg-teal-700"
                          size="sm"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Create User
                        </Button>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {loading ? (
                        <div className="text-center py-6 text-slate-500">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-2"></div>
                          <p>Loading users...</p>
                        </div>
                      ) : filteredActive.length === 0 ? (
                        <div className="text-center py-6 text-slate-500" data-testid="active-users-empty">
                          <Users className="h-12 w-12 mx-auto text-slate-300 mb-2" />
                          <p className="text-slate-600">{activeUsers.length === 0 ? 'No active users found.' : 'No users match the current filter.'}</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {filteredActive.map((activeUser) => (
                            <div key={activeUser.id} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors" data-testid={`active-user-${activeUser.id}`}>
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-1">
                                  <h3 className="font-medium text-slate-800">{activeUser.name}</h3>
                                  <div className="flex gap-1">
                                    {activeUser.roles.map((role) => (
                                      <Badge 
                                        key={role} 
                                        className={`text-xs ${
                                          role === 'admin' ? 'bg-red-100 text-red-800' :
                                          role === 'broker' ? 'bg-blue-100 text-blue-800' :
                                          role === 'underwriter' ? 'bg-purple-100 text-purple-800' :
                                          role === 'jr_underwriter' ? 'bg-fuchsia-100 text-fuchsia-800' :
                                          'bg-gray-100 text-gray-800'
                                        }`}
                                      >
                                        {role === 'jr_underwriter' ? 'jr underwriter' : role}
                                      </Badge>
                                    ))}
                                  </div>
                                  {activeUser.roles.includes('broker') && activeUser.sub_broker_ids?.length > 0 && (
                                    <Badge className="text-xs bg-indigo-100 text-indigo-800">
                                      {activeUser.sub_broker_ids.length} sub-broker{activeUser.sub_broker_ids.length > 1 ? 's' : ''}
                                    </Badge>
                                  )}
                                  {activeUser.access_status === 'restricted' && (
                                    <Badge className="text-xs bg-amber-100 text-amber-800 border border-amber-300" data-testid={`badge-restricted-${activeUser.id}`}>
                                      View-Only
                                    </Badge>
                                  )}
                                  {activeUser.access_status === 'inactive' && (
                                    <Badge className="text-xs bg-red-100 text-red-800 border border-red-300" data-testid={`badge-inactive-${activeUser.id}`}>
                                      Inactive
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-slate-600">
                                  {activeUser.email}
                                  {activeUser.company_name && <span> • {activeUser.company_name}</span>}
                                </p>
                                <p className="text-xs text-slate-500">
                                  Joined {new Date(activeUser.created_at).toLocaleDateString()}
                                  {activeUser.last_login && <span> • Last login: {new Date(activeUser.last_login).toLocaleDateString()}</span>}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                {activeUser.roles.includes('broker') && (
                                  <Button 
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setManagingSubBrokers(activeUser)}
                                    data-testid={`manage-sub-brokers-${activeUser.id}`}
                                  >
                                    <Users className="h-4 w-4 mr-1" />
                                    Team
                                  </Button>
                                )}
                                {/* Impersonate — admins only and never on other admins */}
                                {!activeUser.roles.includes('admin') && activeUser.id !== user?.id && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-orange-300 text-orange-700 hover:bg-orange-50 hover:border-orange-400"
                                    onClick={async () => {
                                      const ok = window.confirm(`Log in as ${activeUser.name}? You'll see the portal exactly as they do. A banner at the top will let you return to your admin session anytime.`);
                                      if (!ok) return;
                                      try {
                                        await impersonateUser(activeUser.id);
                                      } catch (e) {
                                        alert(e?.response?.data?.detail || e?.message || 'Failed to start impersonation.');
                                      }
                                    }}
                                    data-testid={`impersonate-user-${activeUser.id}`}
                                    title={`Log in as ${activeUser.name} to see the portal exactly as they do`}
                                  >
                                    <LogIn className="h-4 w-4 mr-1" />
                                    Log in as
                                  </Button>
                                )}
                                <Button 
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setEditingUser(activeUser)}
                                >
                                  <Edit2 className="h-4 w-4 mr-1" />
                                  Edit
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </>
              );
            })()}
          </div>
        </TabsContent>

        <TabsContent value="applications" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                All Applications
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const pipelineCounts = { all: 0, active: 0, unsubmitted: 0, funded: 0, archived: 0 };
                applications.forEach(app => { pipelineCounts[getPipelineTab(app)] += 1; });
                pipelineCounts.all = applications.length;
                const filtered = pipelineTab === 'all'
                  ? applications
                  : applications.filter(app => getPipelineTab(app) === pipelineTab);
                return (
                  <>
                    <div className="flex items-center gap-1 border-b border-slate-100 mb-5 -mx-2 px-2" role="tablist" aria-label="Pipeline">
                      {[
                        { id: 'active',      label: 'Active' },
                        { id: 'unsubmitted', label: 'Unsubmitted' },
                        { id: 'funded',      label: 'Funded' },
                        { id: 'archived',    label: 'Archived' },
                        { id: 'all',         label: 'All' },
                      ].map(t => {
                        const isActive = pipelineTab === t.id;
                        return (
                          <button
                            key={t.id}
                            role="tab"
                            aria-selected={isActive}
                            onClick={() => setPipelineTab(t.id)}
                            data-testid={`admin-pipeline-tab-${t.id}`}
                            className={`relative px-4 py-2.5 text-sm font-medium transition-colors ${isActive ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                          >
                            {t.label}
                            <span className={`ml-2 inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[11px] font-semibold ${isActive ? 'bg-cyan-100 text-cyan-800' : 'bg-slate-100 text-slate-500'}`}>
                              {pipelineCounts[t.id]}
                            </span>
                            {isActive && <span className="absolute left-2 right-2 -bottom-px h-0.5 bg-cyan-500 rounded-t-full" />}
                          </button>
                        );
                      })}
                    </div>
                    {filtered.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <FileText className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                        <h3 className="text-lg font-medium text-slate-800 mb-2">
                          {pipelineTab === 'active' && 'No active applications'}
                          {pipelineTab === 'unsubmitted' && 'No unsubmitted applications'}
                          {pipelineTab === 'funded' && 'No funded applications'}
                          {pipelineTab === 'archived' && 'No archived applications'}
                          {pipelineTab === 'all' && 'No applications yet'}
                        </h3>
                        <p className="text-slate-600">
                          {pipelineTab === 'active' && 'Applications will appear here as they progress through underwriting.'}
                          {pipelineTab === 'unsubmitted' && 'Incomplete drafts and link submissions without docs appear here.'}
                          {pipelineTab === 'funded' && 'Closed and funded deals will appear here.'}
                          {pipelineTab === 'archived' && 'Declined and withdrawn applications will appear here.'}
                          {pipelineTab === 'all' && 'Every application across every pipeline stage will appear here.'}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {filtered.map((app) => (
                    <div key={app.id} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-medium text-slate-800">{getBusinessName(app)}</h3>
                          <StatusBadge status={app.status} productType={app.product_type} />
                          <Badge variant="outline">{productLabel(app.product_type).toUpperCase()}</Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-600">
                          <span>${getLoanAmount(app).toLocaleString()}</span>
                          <span>•</span>
                          <span className={`font-medium ${app.broker_id ? 'text-blue-700' : 'text-orange-600'}`}>
                            {app.broker_id ? (brokerLookup[app.broker_id] || 'Broker') : 'Unassigned'}
                          </span>
                          <span>•</span>
                          <span>{new Date(app.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setSelectedApplication(app)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Review
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setEditingApplication(app)}
                          title="Edit application details"
                        >
                          <Edit2 className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDeleteApplication(app.id)}
                          title="Delete application"
                          className="text-red-600 hover:text-red-700 hover:border-red-300"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openMessagesForApp(app.id)}
                          title="Open messages for this application"
                          className="relative"
                          data-testid={`admin-msg-${app.id}`}
                        >
                          <MessageSquare className="h-4 w-4" />
                          {unreadCounts[app.id] > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 h-4 min-w-[16px] px-1 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                              {unreadCounts[app.id]}
                            </span>
                          )}
                        </Button>
                      </div>
                    </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="messages" className="mt-6">
          <MessagingPanel
            userRole="admin"
            userId={user.id}
            preSelectedAppId={selectedMessageAppId}
            onAppSelect={setSelectedMessageAppId}
            onBack={previousContext || previousTab ? handleBackFromMessages : null}
          />
        </TabsContent>

        <TabsContent value="commissions" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-emerald-600" />
                Commission Management
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AdminCommissions />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tasks" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Task Manager</CardTitle>
            </CardHeader>
            <CardContent>
              <TaskManager onTaskCountChange={setPendingTaskCount} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="emails" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Email Log</CardTitle>
              <button onClick={fetchEmailLogs} disabled={emailLogsLoading}
                className="h-8 px-3 flex items-center gap-1.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-100 text-xs font-medium transition-all"
                data-testid="refresh-email-logs">
                <RefreshCw className={`h-3.5 w-3.5 ${emailLogsLoading ? 'animate-spin' : ''}`} /> Refresh
              </button>
            </CardHeader>
            <CardContent>
              {emailLogsLoading && emailLogs.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">Loading email logs...</p>
              ) : emailLogs.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">No emails have been sent yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="email-logs-table">
                    <thead>
                      <tr className="border-b border-slate-200 text-left">
                        <th className="pb-2 pr-3 text-xs font-semibold text-slate-500 uppercase">Date</th>
                        <th className="pb-2 pr-3 text-xs font-semibold text-slate-500 uppercase">To</th>
                        <th className="pb-2 pr-3 text-xs font-semibold text-slate-500 uppercase">Subject</th>
                        <th className="pb-2 pr-3 text-xs font-semibold text-slate-500 uppercase">Type</th>
                        <th className="pb-2 pr-3 text-xs font-semibold text-slate-500 uppercase">Sent By</th>
                        <th className="pb-2 text-xs font-semibold text-slate-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {emailLogs.map((log, i) => (
                        <tr key={i} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`email-log-row-${i}`}>
                          <td className="py-2.5 pr-3 text-xs text-slate-500 whitespace-nowrap">
                            {new Date(log.sent_at).toLocaleDateString()} {new Date(log.sent_at).toLocaleTimeString([], {hour: 'numeric', minute:'2-digit'})}
                          </td>
                          <td className="py-2.5 pr-3 text-xs text-slate-700 font-medium max-w-[180px] truncate">{log.to}</td>
                          <td className="py-2.5 pr-3 text-xs text-slate-700 max-w-[250px] truncate">{log.subject}</td>
                          <td className="py-2.5 pr-3">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                              log.email_type === 'ai_composed' ? 'bg-purple-100 text-purple-700' :
                              log.email_type === 'missing_documents' ? 'bg-orange-100 text-orange-700' :
                              log.email_type === 'borrower_invite' ? 'bg-blue-100 text-blue-700' :
                              log.email_type === 'message_notification' ? 'bg-teal-100 text-teal-700' :
                              log.email_type === 'pfs_request' ? 'bg-indigo-100 text-indigo-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {(log.email_type || 'system').replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="py-2.5 pr-3 text-xs text-slate-500">{log.sent_by || 'system'}</td>
                          <td className="py-2.5">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                              log.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {log.status || 'sent'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="toolkit" className="mt-6">
          <BrokerToolkit />
        </TabsContent>

        <TabsContent value="recycle" className="mt-6">
          <RecycleBin token={localStorage.getItem('auth_token')} />
        </TabsContent>
      </Tabs>

      {/* Application Review Modal */}
      {selectedApplication && (
        <ApplicationReview
          application={selectedApplication}
          onClose={() => setSelectedApplication(null)}
          onStatusUpdate={handleApplicationStatusUpdate}
          onOpenMessages={openMessagesForApp}
        />
      )}

      {/* Edit Application Modal */}
      {editingApplication && (
        <EditApplicationModal
          application={editingApplication}
          onClose={() => setEditingApplication(null)}
          onSuccess={() => {
            setEditingApplication(null);
            fetchData();
          }}
          onOpenMessages={openMessagesForApp}
        />
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => { setEditingUser(null); fetchData(); }}
          onSuccess={() => {
            setEditingUser(null);
            fetchData();
          }}
        />
      )}

      {/* Create User Modal */}
      {showCreateUser && (
        <CreateUserModal
          onClose={() => setShowCreateUser(false)}
          onSuccess={() => {
            setShowCreateUser(false);
            fetchData();
          }}
        />
      )}

      {/* Sub-Broker Manager Modal */}
      {managingSubBrokers && (
        <SubBrokerManager
          broker={managingSubBrokers}
          allBrokers={allBrokers}
          onClose={() => setManagingSubBrokers(null)}
          onUpdate={fetchData}
        />
      )}

      <DashboardAIAssistant onAction={handleAIAction} />
    </div>
  );
};

export default AdminDashboard;